import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import Button, { buttonVariants } from 'components/partials/button/button';
import type { ButtonSize, ButtonVariant } from 'components/partials/button/button';

import { renderModal } from 'helpers/modal-render';
import {
  restartCourse,
  startCourse,
} from 'containers/user/learn/containers/content/cources/courses.utils';

import { COURSE_STATUSES } from 'containers/user/learn/containers/content/cources/course.constants';

interface CourseLike {
  id?: string | number;
  name?: string;
  firstLessonId?: string | number;
  status?: string;
}

interface CourseControlButtonsProps {
  course: CourseLike;
  userId?: string;
  size?: ButtonSize;
}

interface ButtonData {
  label: string;
  variant?: ButtonVariant;
  onClick?: () => void;
}

const CourseControlButtons = ({
  course,
  userId,
  size = 'sm',
}: CourseControlButtonsProps): React.JSX.Element => {
  const { id: courseId, name, firstLessonId, status } = course;
  const navigate = useNavigate();

  const getButtonData = (courseStatus?: string): ButtonData => {
    switch (courseStatus) {
      case COURSE_STATUSES.IN_PROGRESS:
        return { label: 'Continue' };
      case COURSE_STATUSES.DONE:
        return {
          label: 'Recap',
          variant: 'reserve-outlined',
        };
      default:
        return {
          onClick: () => {
            startCourse({ userId, courseId });
          },
          label: 'Start Course',
        };
    }
  };

  const { label, onClick } = getButtonData(status);

  const handleRetake = (): void => {
    renderModal({
      headerText: `Are you sure you want to retake ${name} course?`,
      text: 'This action will reset your previous progress, and you`ll start from the beginning.',
      onConfirmCallback: async () => {
        await restartCourse({
          userId,
          courseId,
        });
        navigate(`/learn/courses/${courseId}/lesson/${firstLessonId}`);
      },
      onConfirmButtonLabel: 'Retake Course',
    });
  };

  return (
    <div className="flex items-center gap-2">
      {(status === COURSE_STATUSES.DONE ||
        status === COURSE_STATUSES.IN_PROGRESS) && (
        <Button variant="reserve-outlined" size={size} onClick={handleRetake}>
          Retake
        </Button>
      )}

      <RouterLink
        to={`/learn/courses/${courseId}/lesson/${firstLessonId}`}
        onClick={onClick}
        className={classNames(
          buttonVariants({ variant: 'brand-filled', size }),
          'inline-flex items-center justify-center no-underline'
        )}
      >
        {label}
      </RouterLink>
    </div>
  );
};

export default CourseControlButtons;
